import type {
  ServiceCategoryId,
  ServicePageCalloutContent,
  ServicePageContentModel,
  ServicePageSectionContent,
  ServicePartnerBrand,
} from './types'

interface ServiceDefinition {
  serviceId: string
  pageName: string
  category: ServiceCategoryId
  canonicalPath: string
  description: string
  subheading: string
  intro: string
  sectionHeading: string
  features: readonly string[]
  aliases?: readonly string[]
  partnerBrand?: ServicePartnerBrand
  callout?: ServicePageCalloutContent
  sections?: readonly ServicePageSectionContent[]
  showOnlinePrintCta?: boolean
}

const categoryDetails: Record<ServiceCategoryId, { label: string; slug: string }> = {
  'copy-print': { label: 'Copy & Print', slug: 'copy-print' },
  'home-business': { label: 'Home & Business', slug: 'home-business' },
  shipping: { label: 'Shipping', slug: 'shipping' },
  packing: { label: 'Packing', slug: 'packing' },
}

const serviceDefinitions: readonly ServiceDefinition[] = [
  {
    serviceId: 'fedex-shipping',
    pageName: 'FedEx Shipping',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/FedEx-Shipping',
    aliases: ['/Shipping/FedExShipping', '/Shipping/FedEx-Shipping', '/Pack-Ship/Shipping/FedExShipping'],
    description: 'FedEx shipping, packing assistance, and eligible package drop-offs at Pack Print Mail in Waxahachie, Texas.',
    subheading: 'Your local resource for FedEx shipping, packing support, and convenient service',
    intro: 'Bring your shipment to Pack Print Mail for local help comparing available FedEx services. Our team can help with packing, labeling, and selecting a service based on destination and timing.',
    sectionHeading: 'FedEx services available at Pack Print Mail',
    features: ['FedEx Ground®', 'FedEx Home Delivery®', 'FedEx Express services', 'International FedEx options', 'Eligible prepaid FedEx drop-offs', 'Professional packing support'],
    partnerBrand: { name: 'FedEx', descriptor: 'Authorized ShipCenter', variant: 'fedex' },
    callout: { title: 'Already have a FedEx label?', body: 'Bring eligible, sealed, prepaid FedEx packages to the store for convenient drop-off assistance. Carrier acceptance rules apply.' },
  },
  {
    serviceId: 'usps-shipping',
    pageName: 'US Postal Service',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/US-Postal-Service',
    aliases: ['/services/shipping/usps-shipping'],
    description: 'USPS mailing, postage, and shipping assistance at Pack Print Mail in Waxahachie, Texas.',
    subheading: 'Convenient USPS mailing and shipping without another stop',
    intro: 'Pack Print Mail helps customers prepare eligible letters and packages for USPS mailing. Ask our team about the service, postage, tracking, and documentation appropriate for your shipment.',
    sectionHeading: 'Postal services available',
    features: ['First-Class Mail® and stamps', 'Priority Mail®', 'Priority Mail Express®', 'Certified Mail®', 'Media Mail® when eligible', 'USPS tracking options', 'International mail options'],
    partnerBrand: { name: 'USPS', descriptor: 'Postal shipping services', variant: 'usps' },
  },
  {
    serviceId: 'dhl-shipping',
    pageName: 'DHL Shipping',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/DHL-Shipping',
    description: 'DHL international shipping assistance from Pack Print Mail in Waxahachie, Texas.',
    subheading: 'International delivery options with local counter support',
    intro: 'For an international shipment, Pack Print Mail can help you review available DHL options and prepare the package for carrier acceptance. Service availability depends on the destination and contents.',
    sectionHeading: 'How we can help with DHL shipping',
    features: ['International service options', 'Shipment preparation', 'Packaging assistance', 'Basic customs-document guidance', 'Eligible prepaid DHL drop-offs'],
    partnerBrand: { name: 'DHL', descriptor: 'International shipping', variant: 'dhl' },
  },
  {
    serviceId: 'international-shipping',
    pageName: 'International Shipping',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/International-Shipping',
    description: 'International package preparation and carrier options from Pack Print Mail in Waxahachie, Texas.',
    subheading: 'Practical help preparing shipments for destinations outside the United States',
    intro: 'International shipments may require carrier-specific forms and can be subject to destination restrictions, duties, and taxes. Our team can help prepare eligible packages and explain the information needed at the counter.',
    sectionHeading: 'International shipping support',
    features: ['Multiple carrier options', 'Protective international packing', 'Commercial invoice preparation help', 'Address and label review', 'Service-level comparisons'],
    callout: { title: 'Before you visit', body: 'Bring the destination address, a clear description and value for each item, and any deadline. The destination country determines what can be accepted.' },
  },
  {
    serviceId: 'package-drop-offs',
    pageName: 'Package Drop-Offs',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/Package-Drop-Offs',
    description: 'Convenient drop-off assistance for eligible prepaid FedEx, USPS, and DHL packages in Waxahachie.',
    subheading: 'A convenient local stop for eligible prepaid packages',
    intro: 'Pack Print Mail accepts eligible, sealed, prepaid packages for participating carriers. Packages must meet the carrier’s labeling, size, condition, and acceptance requirements.',
    sectionHeading: 'Preparing a package for drop-off',
    features: ['Attach a readable prepaid label', 'Seal the package before arrival', 'Remove or cover conflicting old labels', 'Keep your tracking number', 'Ask about packing help before applying the label'],
    callout: { title: 'Need a shipping label?', body: 'A prepaid drop-off and a new retail shipment are different services. Bring the package and destination details if you need us to create a shipment.' },
  },
  {
    serviceId: 'package-receiving',
    pageName: 'Package Receiving',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/Package-Receiving',
    description: 'Staffed package receiving options at Pack Print Mail in Waxahachie, Texas.',
    subheading: 'A staffed local delivery address for eligible packages',
    intro: 'Package receiving can help when you do not want a delivery left unattended. Contact the store before directing a shipment to Pack Print Mail so we can confirm enrollment, addressing, fees, and package limits.',
    sectionHeading: 'Package receiving benefits',
    features: ['Staffed delivery location', 'Carrier package acceptance during business hours', 'Pickup at the store', 'Clear addressing instructions', 'Options for frequent recipients'],
  },
  {
    serviceId: 'postage-stamps',
    pageName: 'Postage Stamps',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/Postage-Stamps',
    description: 'Purchase postage stamps and get basic mailing assistance at Pack Print Mail in Waxahachie.',
    subheading: 'Stamps and mailing help from your neighborhood service center',
    intro: 'Stop by Pack Print Mail when you need postage stamps or help preparing common mail pieces. For unusual sizes, weights, or international mail, bring the item so we can review available options.',
    sectionHeading: 'Mailing help at the counter',
    features: ['Postage stamps', 'Envelope and mailer options', 'Basic postage guidance', 'USPS shipping services', 'Packing supplies'],
  },
  {
    serviceId: 'antique-shipping',
    pageName: 'Antique Shipping',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/Antique-Shipping',
    description: 'Careful packing and shipping assistance for antiques from Pack Print Mail in Waxahachie.',
    subheading: 'Careful preparation for valuable, delicate, and irreplaceable items',
    intro: 'Antiques often need more protection than an ordinary shipment. Bring the unpacked item when possible so our team can evaluate its size, weight, fragility, and appropriate packing approach.',
    sectionHeading: 'Antique shipping preparation',
    features: ['Item-specific packing review', 'Protective cushioning', 'Double-boxing when appropriate', 'Custom box options', 'Carrier and service comparison'],
  },
  {
    serviceId: 'artwork-shipping',
    pageName: 'Artwork Shipping',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/Artwork-Shipping',
    description: 'Packing and shipping support for framed art, prints, and other artwork in Waxahachie.',
    subheading: 'Protective packing for framed, unframed, and delicate artwork',
    intro: 'Artwork varies widely in size, construction, and fragility. Pack Print Mail can review the piece and recommend materials and carrier options appropriate for the shipment.',
    sectionHeading: 'Artwork packing options',
    features: ['Framed and unframed art', 'Corner and surface protection', 'Protective cushioning', 'Specialty or custom boxes', 'Domestic and international carrier options'],
  },
  {
    serviceId: 'electronics-shipping',
    pageName: 'Electronics Shipping',
    category: 'shipping',
    canonicalPath: '/Pack-Ship/Shipping/Electronics-Shipping',
    description: 'Protective packing and shipping assistance for computers and electronics in Waxahachie.',
    subheading: 'Extra care for computers, devices, and delicate electronics',
    intro: 'Electronics can be vulnerable to shock, movement, and pressure during transit. Bring the device and any original packaging so our team can recommend a secure packing approach and review carrier restrictions.',
    sectionHeading: 'Electronics we can help prepare',
    features: ['Laptop and desktop computers', 'Tablets and mobile devices', 'Audio and video equipment', 'Small office electronics', 'Multi-layer protective packing'],
  },
  {
    serviceId: 'packing-services',
    pageName: 'Professional Packing',
    category: 'packing',
    canonicalPath: '/Pack-Ship/Packing/Professional-Packing',
    description: 'Professional packing help for everyday, fragile, valuable, and oversized items in Waxahachie.',
    subheading: 'Big or small, we will help prepare it for the journey',
    intro: 'Good packing starts with the item, not a one-size-fits-all box. Bring your item to Pack Print Mail and our team will recommend a box, cushioning, sealing method, and carrier-ready preparation.',
    sectionHeading: 'Professional packing services',
    features: ['Fragile-item packing', 'Electronics packing', 'Artwork and collectible packing', 'Double-boxing when appropriate', 'Custom box making', 'Packing for domestic or international shipping'],
  },
  {
    serviceId: 'packaging-supplies',
    pageName: 'Packaging Supplies',
    category: 'packing',
    canonicalPath: '/Pack-Ship/Packing/Packaging-Supplies',
    description: 'Boxes, mailers, tape, cushioning, and moving supplies at Pack Print Mail in Waxahachie.',
    subheading: 'The right materials for packing, mailing, storage, and moving',
    intro: 'Using a right-sized box and suitable cushioning can protect an item and help avoid unnecessary dimensional-weight charges. Visit the store to match your item with available supplies.',
    sectionHeading: 'Packing and moving supplies',
    features: ['Corrugated boxes in multiple sizes', 'Specialty shipping boxes', 'Bubble cushioning and packing fill', 'Packing tape and tape dispensers', 'Mailing tubes and padded mailers', 'Custom-order quantities by request'],
  },
  {
    serviceId: 'custom-box-making',
    pageName: 'Custom Box Making',
    category: 'packing',
    canonicalPath: '/Pack-Ship/Packing/Custom-Box-Making',
    description: 'Custom-made boxes for unusually sized or shaped items at Pack Print Mail in Waxahachie.',
    subheading: 'A right-sized packing solution when a stock box will not work',
    intro: 'An oversized stock box can require excess fill and may increase shipping cost. Pack Print Mail can evaluate eligible items and build a custom corrugated box suited to the dimensions and packing plan.',
    sectionHeading: 'When a custom box helps',
    features: ['Long or unusually shaped items', 'Artwork and framed pieces', 'Equipment and electronics', 'Nested or double-box solutions', 'Reduced empty space compared with oversized stock boxes'],
  },
  {
    serviceId: 'mailbox-rental',
    pageName: 'Mailbox Rental',
    category: 'home-business',
    canonicalPath: '/Home-Business/Mailbox-Rental',
    description: 'Private mailbox rental with a local Waxahachie street address at Pack Print Mail.',
    subheading: 'A secure and convenient address for personal or business mail',
    intro: 'A private mailbox provides a staffed delivery address and an alternative to receiving mail at home. Contact Pack Print Mail for current box sizes, pricing, identification requirements, and availability.',
    sectionHeading: 'Mailbox rental features',
    features: ['Local street address', 'Mail and eligible carrier package receiving', 'Staffed location', 'Options for individuals and businesses', 'Flexible rental terms subject to availability'],
  },
  {
    serviceId: 'digital-mailbox-rental',
    pageName: 'Digital Mailbox Rental',
    category: 'home-business',
    canonicalPath: '/Home-Business/Digital-Mailbox-Rental',
    description: 'Digital mailbox options connected to the Pack Print Mail location in Waxahachie.',
    subheading: 'View and manage eligible mail when you are away from the store',
    intro: 'Digital mailbox service combines a local receiving address with online mail-management tools. Platform features, plans, identity verification, and forwarding charges vary, so contact the store before signing up.',
    sectionHeading: 'Common digital mailbox capabilities',
    features: ['Online mail notifications', 'Remote mail-management requests', 'Forwarding options', 'Document scan requests where available', 'Local receiving address'],
  },
  {
    serviceId: 'ipostal1',
    pageName: 'iPostal1',
    category: 'home-business',
    canonicalPath: '/Home-Business/iPostal1',
    aliases: ['/ipostal1'],
    description: 'iPostal1 digital mailbox service connected to Pack Print Mail in Waxahachie, Texas.',
    subheading: 'Manage eligible mail and packages through the iPostal1 platform',
    intro: 'Pack Print Mail participates as a mailbox location for iPostal1. Customers use the provider’s platform to view incoming mail and request available actions such as forwarding, pickup, or scanning.',
    sectionHeading: 'How iPostal1 works',
    features: ['Choose the Pack Print Mail location', 'Complete the provider’s identity verification', 'Receive eligible mail and packages', 'Manage requests online', 'Pick up or forward items according to your plan'],
    callout: { title: 'Provider account required', body: 'Plans, billing, verification, and online features are managed through iPostal1. Contact us with questions about using our store as the receiving location.' },
  },
  {
    serviceId: 'anytime-mailbox',
    pageName: 'Anytime Mailbox',
    category: 'home-business',
    canonicalPath: '/Home-Business/Anytime-Mailbox',
    description: 'Anytime Mailbox virtual mailbox service connected to Pack Print Mail in Waxahachie.',
    subheading: 'Online mail management with a participating local receiving location',
    intro: 'Pack Print Mail is represented as a participating location for Anytime Mailbox. Provider plans and features vary; review current enrollment, verification, forwarding, scanning, and pickup terms before subscribing.',
    sectionHeading: 'Virtual mailbox workflow',
    features: ['Enroll through the provider', 'Complete identity verification', 'Use the assigned mailing address', 'Review mail notifications online', 'Request available pickup, forwarding, or scanning actions'],
  },
  {
    serviceId: 'document-scanning',
    pageName: 'Document Scanning',
    category: 'home-business',
    canonicalPath: '/Home-Business/Document-Scanning',
    description: 'Document scanning to common digital formats at Pack Print Mail in Waxahachie.',
    subheading: 'Turn paper documents into useful digital files',
    intro: 'Bring paper documents to Pack Print Mail for scanning to an appropriate digital format. Pricing and turnaround depend on page count, condition, size, color, organization, and the requested delivery method.',
    sectionHeading: 'Document scanning options',
    features: ['Black-and-white or color scanning', 'PDF and common image formats', 'Email or customer-provided storage delivery', 'Single- or multi-page documents', 'File organization by request'],
  },
  {
    serviceId: 'shredding-services',
    pageName: 'Document Shredding',
    category: 'home-business',
    canonicalPath: '/Home-Business/Document-Shredding',
    aliases: ['/services/home-business/shredding-services'],
    description: 'Secure document shredding service at Pack Print Mail in Waxahachie, Texas.',
    subheading: 'A convenient way to dispose of sensitive paper documents',
    intro: 'Pack Print Mail provides a local document shredding option for eligible paper records. Contact the store for current pricing, volume limits, accepted materials, and the shredding process.',
    sectionHeading: 'Good candidates for shredding',
    features: ['Old financial paperwork', 'Outdated business records', 'Personal correspondence', 'Documents containing account information', 'Routine household paper files'],
  },
  {
    serviceId: 'fax-services',
    pageName: 'Fax Services',
    category: 'home-business',
    canonicalPath: '/Home-Business/Fax-Services',
    description: 'Local fax sending and receiving assistance at Pack Print Mail in Waxahachie.',
    subheading: 'Send or receive eligible fax documents without owning a fax machine',
    intro: 'Bring your document and destination fax number to the store for sending assistance. Call before arranging an incoming fax so our team can provide current instructions, pricing, and identification requirements.',
    sectionHeading: 'Fax service help',
    features: ['Domestic fax sending', 'International faxing when available', 'Incoming fax arrangements', 'Transmission confirmation', 'Copying and scanning support'],
  },
  {
    serviceId: 'notary-services',
    pageName: 'Notary Public',
    category: 'home-business',
    canonicalPath: '/Home-Business/Notary-Public',
    aliases: ['/services/home-business/notary-services'],
    description: 'Convenient notary public service at Pack Print Mail in Waxahachie; call ahead for availability.',
    subheading: 'Local notary help for eligible documents',
    intro: 'Call before visiting to confirm notary availability, pricing, acceptable identification, witness needs, and whether your document is eligible. Do not sign the document until the notary instructs you to do so.',
    sectionHeading: 'Before your notary visit',
    features: ['Bring valid government-issued identification', 'Bring the complete unsigned document', 'Confirm whether witnesses are required', 'Ensure every signer can appear as required', 'Ask about remote online notary availability separately'],
    callout: { title: 'Not legal advice', body: 'A notary verifies identity and witnesses eligible signatures but cannot choose forms or provide legal advice. Contact the document recipient or an attorney with legal questions.' },
  },
  {
    serviceId: 'passport-photos',
    pageName: 'Passport Photos',
    category: 'home-business',
    canonicalPath: '/Home-Business/Passport-Photos',
    description: 'Passport and identification photo service at Pack Print Mail in Waxahachie, Texas.',
    subheading: 'Convenient identification photos prepared while you wait',
    intro: 'Pack Print Mail can prepare photos for common passport and identification applications. Requirements vary by agency and country, so bring the current specification when it differs from a standard U.S. passport photo.',
    sectionHeading: 'Photo service includes',
    features: ['Photo capture at the store', 'Cropping to the requested common format', 'Printed photo set', 'Basic background and framing review', 'Additional copies by request'],
  },
  {
    serviceId: 'office-supplies',
    pageName: 'Office Supplies',
    category: 'home-business',
    canonicalPath: '/Home-Business/Office-Supplies',
    description: 'Everyday office, mailing, and document supplies at Pack Print Mail in Waxahachie.',
    subheading: 'Convenient essentials for home offices and local businesses',
    intro: 'Pack Print Mail carries a practical selection of office and mailing supplies. Inventory changes, so call ahead when you need a particular size, quantity, brand, or specialty item.',
    sectionHeading: 'Common supplies',
    features: ['Envelopes and mailers', 'Packing and shipping supplies', 'Paper and document supplies', 'Labels and tape', 'Basic desk and organization items'],
  },
  {
    serviceId: 'rubber-stamps',
    pageName: 'Rubber Stamps',
    category: 'home-business',
    canonicalPath: '/Home-Business/Rubber-Stamps',
    description: 'Custom rubber stamp ordering through Pack Print Mail in Waxahachie, Texas.',
    subheading: 'Custom stamps for routine business and personal tasks',
    intro: 'Order a custom stamp with the wording and layout you need. Bring the text, logo, or example and our team can review size, ink, proof, and turnaround options.',
    sectionHeading: 'Popular custom stamp uses',
    features: ['Return addresses', 'Business names and contact details', 'Approval and routing marks', 'Deposit and accounting stamps', 'Custom text and simple logos'],
  },
  // {
  //   serviceId: 'digital-fingerprinting',
  //   pageName: 'Digital Fingerprinting',
  //   category: 'home-business',
  //   canonicalPath: '/Home-Business/Digital-Fingerprinting',
  //   description: 'Digital fingerprinting service at Pack Print Mail in Waxahachie; contact the store before visiting.',
  //   subheading: 'Digital fingerprint capture for eligible agency and employment requests',
  //   intro: 'Pack Print Mail currently lists digital fingerprinting as an offered service. Requirements depend on the requesting organization, so contact the store before visiting to confirm appointment availability, identification, forms, and submission method.',
  //   sectionHeading: 'Prepare for fingerprinting',
  //   features: ['Confirm the requesting agency’s instructions', 'Bring required identification', 'Bring any service code or paperwork', 'Ask whether an appointment is required', 'Confirm current pricing and payment options'],
  //   callout: { title: 'Call before visiting', body: 'Fingerprinting systems and agency workflows are not interchangeable. We will confirm whether the store can process your specific request before your visit.' },
  // },
  {
    serviceId: 'printing-copying',
    pageName: 'Print & Document Services',
    category: 'copy-print',
    canonicalPath: '/Copy-Print/Print-Document-Services',
    description: 'Printing, copying, and document finishing with local help from Pack Print Mail in Waxahachie.',
    subheading: 'A local one-stop shop for printed documents and finishing',
    intro: 'From a single document to a larger business project, Pack Print Mail can help with file review, paper selection, printing, copying, and finishing. Final pricing depends on quantity, stock, color, size, setup, and turnaround.',
    sectionHeading: 'Print and document services',
    features: ['Black-and-white and color printing', 'Letter, legal, and ledger sizes', 'Single- and double-sided output', 'Collating, binding, and laminating', 'File preparation and design help', 'Print-on-demand options'],
    showOnlinePrintCta: true,
  },
  {
    serviceId: 'copying-services',
    pageName: 'Copying Services',
    category: 'copy-print',
    canonicalPath: '/Copy-Print/Copying-Services',
    description: 'Color and black-and-white copying services at Pack Print Mail in Waxahachie.',
    subheading: 'Copies for personal, school, organization, and business needs',
    intro: 'Bring your originals or print-ready digital files to Pack Print Mail. We can help select color or black-and-white output, paper, sides, quantity, and finishing for the job.',
    sectionHeading: 'Copying options',
    features: ['Black-and-white copies', 'Color copies', 'Single- or double-sided copies', 'Multiple paper sizes and stocks', 'Collating and finishing', 'Small and larger quantities'],
    showOnlinePrintCta: true,
  },
  {
    serviceId: 'flyers-brochures',
    pageName: 'Flyers & Brochures',
    category: 'copy-print',
    canonicalPath: '/Copy-Print/Flyers-Brochures',
    description: 'Flyer and brochure printing for Waxahachie businesses, organizations, and events.',
    subheading: 'Clear, professional handouts for promotions and information',
    intro: 'Pack Print Mail can print customer-supplied designs or help prepare a file for production. Choose a size, paper, color, fold, quantity, and turnaround that fits the project.',
    sectionHeading: 'Flyer and brochure options',
    features: ['Full-color or black-and-white printing', 'Single- or double-sided layouts', 'Multiple paper stocks', 'Common brochure folds', 'Short-run and quantity printing', 'Design and file-preparation help'],
    showOnlinePrintCta: true,
  },
  {
    serviceId: 'document-finishing',
    pageName: 'Document Finishing',
    category: 'copy-print',
    canonicalPath: '/Copy-Print/Document-Finishing',
    aliases: ['/Home-Business/Document-Finishing'],
    description: 'Binding, laminating, collating, and document finishing at Pack Print Mail in Waxahachie.',
    subheading: 'The finishing touches that make documents easier to use and present',
    intro: 'Finishing turns loose printed pages into a more organized, durable, and professional piece. Bring the project or send the print-ready file so we can review appropriate options.',
    sectionHeading: 'Document finishing options',
    features: ['Collating', 'Stapling', 'Binding', 'Laminating', 'Folding', 'Cutting and trimming'],
    showOnlinePrintCta: true,
  },
  {
    serviceId: 'graphic-design',
    pageName: 'Graphic Design',
    category: 'copy-print',
    canonicalPath: '/Copy-Print/Graphic-Design',
    description: 'Practical graphic design and print-file preparation from Pack Print Mail in Waxahachie.',
    subheading: 'Design help that gets your printed message ready for production',
    intro: 'If your idea is not yet print-ready, Pack Print Mail can help with layout, sizing, text, images, and production setup. Bring your content, logo files, examples, and deadline for an accurate project estimate.',
    sectionHeading: 'Design support for printed materials',
    features: ['Flyers and brochures', 'Postcards and invitations', 'Business forms and handouts', 'Signs and promotional pieces', 'File resizing and corrections', 'Print-ready PDF preparation'],
    showOnlinePrintCta: true,
  },
  {
    serviceId: 'photo-printing',
    pageName: 'Photo Printing',
    category: 'copy-print',
    canonicalPath: '/Copy-Print/Photo-Printing',
    description: 'Convenient photo printing at Pack Print Mail in Waxahachie, Texas.',
    subheading: 'Turn digital pictures into prints you can share and keep',
    intro: 'Bring accessible, high-resolution photo files for the best result. Available sizes, paper, color adjustment, turnaround, and pricing depend on the requested project.',
    sectionHeading: 'Photo printing uses',
    features: ['Family and event photos', 'School and organization projects', 'Display and gift prints', 'Additional copies', 'Basic sizing and file review'],
    showOnlinePrintCta: true,
  },
  {
    serviceId: 'postcard-printing',
    pageName: 'Postcard Printing',
    category: 'copy-print',
    canonicalPath: '/Copy-Print/Postcard-Printing',
    description: 'Custom postcard printing for announcements, promotions, and mailings in Waxahachie.',
    subheading: 'A compact printed piece for messages, invitations, and promotions',
    intro: 'Pack Print Mail can produce postcards from a customer-supplied design or help prepare the artwork. Choose the size, stock, sides, quantity, and mailing approach that fits your project.',
    sectionHeading: 'Popular postcard projects',
    features: ['Business promotions', 'Event announcements', 'Invitations and save-the-dates', 'Thank-you cards', 'Direct-mail pieces', 'Design and file-preparation support'],
    showOnlinePrintCta: true,
  },
]

export const servicePageContent: readonly ServicePageContentModel[] = serviceDefinitions.map((definition) => {
  const category = categoryDetails[definition.category]
  const internalPath = `/services/${category.slug}/${definition.canonicalPath.split('/').at(-1)?.toLocaleLowerCase('en-US')}`
  const sections = definition.sections ?? [
    { paragraphs: [definition.intro] },
    {
      heading: definition.sectionHeading,
      list: { items: definition.features, columns: definition.features.length > 4 ? 2 : 1 },
    },
  ]

  return {
    serviceId: definition.serviceId,
    enabled: true,
    canonicalPath: definition.canonicalPath,
    aliases: [internalPath, ...(definition.aliases ?? [])],
    pageName: definition.pageName,
    parentCategory: definition.category,
    parentCategoryLabel: category.label,
    parentCategoryPath: `/services/${category.slug}`,
    hero: {
      imageSrc: '/images/store-services-hero.png',
      imageAlt: `${definition.pageName} assistance at Pack Print Mail`,
      partnerBrand: definition.partnerBrand,
    },
    heading: `${definition.pageName} in Waxahachie, TX`,
    subheading: definition.subheading,
    seo: {
      title: `${definition.pageName} | Waxahachie, TX | Pack Print Mail`,
      description: definition.description,
      openGraphTitle: `${definition.pageName} | Pack Print Mail`,
      openGraphDescription: definition.description,
    },
    sections,
    callout: definition.callout,
    question: {
      heading: `Have A Question About ${definition.pageName}?`,
      body: 'Call for current availability, pricing, or project requirements. You can also send our local team a message.',
      showTrackingCta: definition.category === 'shipping',
    },
    showOnlinePrintCta: definition.showOnlinePrintCta,
  }
})

export function normalizeServicePath(pathname: string) {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  return normalized.toLocaleLowerCase('en-US')
}

export function findServicePageContent(pathname: string) {
  const requestedPath = normalizeServicePath(pathname)

  return servicePageContent.find((page) =>
    page.enabled && [page.canonicalPath, ...page.aliases].some(
      (candidate) => normalizeServicePath(candidate) === requestedPath,
    ),
  )
}

export function getPreferredServicePath(serviceId: string, fallbackPath: string) {
  return servicePageContent.find((page) => page.enabled && page.serviceId === serviceId)?.canonicalPath ?? fallbackPath
}
